import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ProtocolSettingsComponent } from "../ProtocolSettingsComponent";
import { SnmpSettings, SnmpVersion } from 'DiscoveryEngine.TS/Protocols';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';

@Component({
    selector: 'snmp-v3-settings',
	templateUrl: './snmp-v3-settings.component.html',
	styleUrls: ['./snmp-v3-settings.component.scss']
})
export class SnmpV3SettingsComponent extends ProtocolSettingsComponent<SnmpSettings> implements OnChanges {

	public PasswordInputType = "password";

	public Name: UntypedFormControl;
	public Description: UntypedFormControl;
	public Username: UntypedFormControl;
	public Context: UntypedFormControl;
	public Timeout: UntypedFormControl;
	public Retries: UntypedFormControl;
	public Port: UntypedFormControl;
	public UseGetBulk: UntypedFormControl;
	public IgnoreEngineBoots: UntypedFormControl;
	public AuthProtocol: UntypedFormControl;
	public AuthPassword: UntypedFormControl;
	public AuthPasswordConfirm: UntypedFormControl;
	public EncryptProtocol: UntypedFormControl;
	public EncryptPassword: UntypedFormControl;
	public EncryptPasswordConfirm: UntypedFormControl;
	public ShowChars: UntypedFormControl;
	public IpScope: UntypedFormControl;
	public IsDefault: UntypedFormControl;

	private AuthPasswordChanged: boolean;
	private EncryptPasswordChanged: boolean;

	constructor(private fb: UntypedFormBuilder) {
		super();
		this.BuildForm();
	}

	protected BuildForm(): void {
		this.PasswordInputType = "password";

		if (this.Settings) {
			this.Name = new UntypedFormControl(this.Settings.Name, Validators.required);
			this.Description = new UntypedFormControl(this.Settings.Description);
			this.Username = new UntypedFormControl(this.Settings.Username, Validators.required);
			this.Context = new UntypedFormControl(this.Settings.Context);
			this.Timeout = new UntypedFormControl(this.Settings.Timeout, Validators.compose([Validators.required, this.minValue(200), this.maxValue(60000)]));
			this.Retries = new UntypedFormControl(this.Settings.Retries, Validators.compose([Validators.required, this.minValue(0), this.maxValue(10)]));
			this.Port = new UntypedFormControl(this.Settings.Port, Validators.compose([Validators.required, this.minValue(1), this.maxValue(65535)]));
			this.UseGetBulk = new UntypedFormControl(this.Settings.UseGetBulk);
			this.IgnoreEngineBoots = new UntypedFormControl(this.Settings.IgnoreEngineBoots);
			this.AuthProtocol = new UntypedFormControl(this.Settings.AuthProtocol ? this.Settings.AuthProtocol : "None");
			this.AuthPassword = new UntypedFormControl(this.hidePassword(this.Settings.AuthPassword));
			this.AuthPasswordConfirm = new UntypedFormControl(this.hidePassword(this.Settings.AuthPassword));
			this.EncryptProtocol = new UntypedFormControl(this.Settings.EncryptProtocol ? this.Settings.EncryptProtocol : "None");
			this.EncryptPassword = new UntypedFormControl(this.hidePassword(this.Settings.EncryptPassword));
			this.EncryptPasswordConfirm = new UntypedFormControl(this.hidePassword(this.Settings.EncryptPassword));
			this.ShowChars = new UntypedFormControl(false);
			this.IpScope = new UntypedFormControl(this.Settings.IpScope.ToString());
			this.IsDefault = new UntypedFormControl(this.Settings.IsDefault);

			this.enableDisableControls();

			this.AuthPasswordChanged = false;
			this.EncryptPasswordChanged = false;

			this.SettingsForm = this.fb.group({
					Name: this.Name,
					Description: this.Description,
					Username: this.Username,
					Context: this.Context,
					Timeout: this.Timeout,
					Retries: this.Retries,
					Port: this.Port,
					UseGetBulk: this.UseGetBulk,
					IgnoreEngineBoots: this.IgnoreEngineBoots,
					AuthProtocol: this.AuthProtocol,
					AuthPassword: this.AuthPassword,
					AuthPasswordConfirm: this.AuthPasswordConfirm,
					EncryptProtocol: this.EncryptProtocol,
					EncryptPassword: this.EncryptPassword,
					EncryptPasswordConfirm: this.EncryptPasswordConfirm,
					ShowChars: this.ShowChars,
					IpScope: this.IpScope,
					IsDefault: this.IsDefault,
				},
				{
					validator: Validators.compose([
						this.equalTo(this.AuthPassword, this.AuthPasswordConfirm, "authPasswordsEqual"),
						this.equalTo(this.EncryptPassword, this.EncryptPasswordConfirm, "encryptPasswordsEqual")])
				});
		}
		else {
			this.SettingsForm = null;
			this.Name = null;
			this.Description = null;
			this.Username = null;
			this.Context = null;
			this.Timeout = null;
			this.Retries = null;
			this.Port = null;
			this.UseGetBulk = null;
			this.IgnoreEngineBoots = null;
			this.AuthProtocol = null;
			this.AuthPassword = null;
			this.AuthPasswordConfirm = null;
			this.EncryptProtocol = null;
			this.EncryptPassword = null;
			this.EncryptPasswordConfirm = null;
			this.ShowChars = null;
			this.IpScope = null;
			this.IsDefault = null;
		}
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.ShowChars, this.AllowShowChars);
		this.enableDisableFormControl(this.AuthPassword, this.AuthPasswordsEnabled);
		this.enableDisableFormControl(this.AuthPasswordConfirm, this.AuthPasswordsEnabled);
		this.enableDisableFormControl(this.EncryptPassword, this.EncryptPasswordsEnabled);
		this.enableDisableFormControl(this.EncryptPasswordConfirm, this.EncryptPasswordsEnabled);
	}


	public ngOnChanges(changes: SimpleChanges): void {
		this.BuildForm();
	}

	public get AuthPasswordsEnabled(): boolean {
		return (this.AuthProtocol.value != "None");
	}

	public get EncryptPasswordsEnabled(): boolean {
		return (this.EncryptProtocol.value != "None");
	}

	public get invalidTempChanges(): string[] {
		return ["SNMP V3 Users"];
	}

	public applyTempChanges(): void {
		this.Settings.Name = this.Name.value;
		this.Settings.Description = this.Description.value;
		this.Settings.Version = SnmpVersion.Ver3;
		this.Settings.Username = this.Username.value;
		this.Settings.Context = this.Context.value;
		this.Settings.Timeout = parseInt(this.Timeout.value);
		this.Settings.Retries = parseInt(this.Retries.value);
		this.Settings.Port = parseInt(this.Port.value);
		this.Settings.UseGetBulk = this.UseGetBulk.value;
		this.Settings.IgnoreEngineBoots = this.IgnoreEngineBoots.value;
		this.Settings.AuthProtocol = ((this.AuthProtocol.value == "None") ? "" : this.AuthProtocol.value);
		if (this.AuthPasswordChanged) {
			this.Settings.AuthPassword = StringUtils.ObfuscateString2(this.AuthPassword.value);
		}
		this.Settings.EncryptProtocol = ((this.EncryptProtocol.value == "None") ? "" : this.EncryptProtocol.value);
		if (this.EncryptPasswordChanged) {
			this.Settings.EncryptPassword = StringUtils.ObfuscateString2(this.EncryptPassword.value);
		}
		this.Settings.IpScope.FromString(this.IpScope.value);
		this.Settings.IsDefault = this.IsDefault.value;

		this.BuildForm();
		this.OnApplyTempChanges();
	}

}
